 
 
 
 
 
 

/**
 * Reverse geocode service implementation.
 *
 * It allows to convert geographical coordinates into textual address representation using the TomTom's
 * [Reverse Geocode service](https://developer.tomtom.com/online-search/online-search-documentation-reverse-geocoding/reverse-geocode).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "ReverseGeocode/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *   {{#crossLink "ReverseGeocode/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * The final response from service is a JSON object with the following structure:
 *
 * ```js
 * {
 *   address: {
 *     buildingNumber: 19,
 *     streetNumber: 19,
 *     landmark: "",
 *     street: "N 2nd St",
 *     streetName: "N 2nd St",
 *     streetNameAndNumber: "",
 *     speedLimit: "",
 *     countryCode: "US",
 *     countrySubdivision: "CA",
 *     countrySecondarySubdivision: "Santa Clara",
 *     countryTertiarySubdivision: "",
 *     municipality: "San Jose",
 *     postalCode: "95113",
 *     municipalitySubdivision: "San Francisco Bay Area",
 *     freeformAddress: "19 N 2nd St, San Jose, CA, 95113"
 *   },
 *   position: "37.337083, -121.8898",
 *   roadUse: ["LocalStreet"]
 * }
 * ```
 *
 * @example
 * ```js
 * function callbackFn(response) {
 *   console.log(response);
 * }
 * tomtom.reverseGeocode()
 *   .position("51.759,19.449")
 *   .go()
 *   .then(callbackFn);
 * ```
 *
 * @class ReverseGeocode
 * @module Services
 * @uses Chain
 * @uses KeyMixin
 * @uses ProtocolMixin
 * @uses RadiusMixin
 * @uses PositionMixin
 * @uses SpatialKeysMixin
 * @uses HeadingMixin
 *
 * @constructor
 * @param {Object}   [options] Options to be passed to the reverse geocode call,
 *     or Array of such options objects to perform batch request.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 *
 * @param {String}   [options.key] An API Key issued from <a href="http://developer.tomtom.com">Developer Portal</a>
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 *
 * @param {String|Array|Object} [options.position] Specific longitude and latitude of the location where the address
 *     is supposed to be found.
 *     The possible formats are explained in more detail in the
 *     {{#crossLink "ReverseGeocode/position:method"}}{{/crossLink}} method.
 *     This option is required to make the call.
 * @param {String} [options.key] API Key valid for requested service.
 * @param {Boolean} [options.spatialKeys=false] If is enable the response will also contain a proprietary geo-spatial
 *     key information for a specified location
 * @param {Boolean} [options.returnSpeedLimit=false] To enable return of the posted speed limit (where
 *     available).
 * @param {Number} [options.heading] The directional heading of the vehicle in degrees, for travel along a
 *     segment of roadway.
 *     `0` is North, `90` is East and so on, values range from `-360` to `360`.
 *     The precision can include up to one decimal place.
 * @param {Number} [options.radius=10000] The maximum distance in meters from the specified position to consider
 * @param {String} [options.number] If a street number is sent in along with the request, the response
 *     may include the side of the street (Left/Right) and also an offset position for that street number.
 * @param {Boolean} [options.returnRoadUse=false] Requires to include a road use array for reversegeocodes at street level
 * @param {String[]|String} [options.roadUse] To restrict the result to one or a group of the following road uses:
 *      `LimitedAccess`, `Arterial`, `Terminal`, `Ramp`, `Rotary`, `LocalStreet`.
 */

define(['./reverseGeocodeBase', '../validators', '../converters'],
    function(reverseGeocodeBase, validators, converters) {
        'use strict';

        var ReverseGeocodeBasePrototype = reverseGeocodeBase.prototype.constructor;

        function ReverseGeocode(options) {
            ReverseGeocodeBasePrototype.call(this, options);

            this.fields.type = {
                defaultValue: this.TYPE.reverseGeocode,
                visible: false
            };

            /**
             * Sets or gets the value of the option `returnSpeedLimit`.
             *
             * Allows, if possible, to receive the speed limit information at the given location.
             *
             * @method returnSpeedLimit
             * @chainable
             * @param {Boolean} [returnSpeedLimit] Enable or disable the feature
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {Boolean} The same service instance or the current option value if no argument was given
             */
            this.fields.returnSpeedLimit = {
                validator: validators.bool
            };

            /**
             * Sets or gets the value of the option `number`.
             *
             * If a street number is sent in along with the request, the response may
             * include the side of the street (Left/Right) and also an offset position for that street number.
             *
             * @method number
             * @chainable
             * @param {String} [number] Street number as a string
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            this.fields.number = {
                validator: validators.string
            };

            /**
             * Sets or gets the value of the option `returnRoadUse`.
             *
             * Requires to include a road use array for reversegeocodes at street level.
             *
             * @method returnRoadUse
             * @chainable
             * @param {Boolean} [returnRoadUse] Enable or disable the feature
             * @throws {TypeError} if the given argument cannot be converted into a valid value
             * @return {Boolean} The same service instance or the current option value if no argument was given
             */
            this.fields.returnRoadUse = {
                validator: validators.bool
            };

            /**
             * Sets or gets the value of the option `roadUse`.
             *
             * Use this option if you want to restrict the result to one or a group of the following road uses:
             * - `"LimitedAccess"`
             * - `"Arterial"`
             * - `"Terminal"`
             * - `"Ramp"`
             * - `"Rotary"`
             * - `"LocalStreet"`
             *
             * @method roadUse
             * @chainable
             * @param {String[]|String} [roadUse] An array of strings or just one string with comma separated values
             * @throws {TypeError} if the given argument cannot be converted into a valid value
             * @return {String[]|String} The same service instance or the current option value if no argument was given
             */
            this.fields.roadUse = {
                converter: converters.roadUse,
                validator: validators.roadUse
            };

        }

        ReverseGeocode.prototype = new ReverseGeocodeBasePrototype();
        ReverseGeocode.prototype.constructor = ReverseGeocode;

        return function(options) {
            return (new ReverseGeocode(options)).construct();
        };
    });



// WEBPACK FOOTER //
// ./services/reverseGeocode/reverseGeocode.js